import { useTranslations } from "next-intl";
import React from "react";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";

interface TimeSlot {
  start: string;
  end: string;
}

interface BusinessHours {
  sunday: TimeSlot[];
  monday: TimeSlot[];
  tuesday: TimeSlot[];
  wednesday: TimeSlot[];
  thursday: TimeSlot[];
  friday: TimeSlot[];
  saturday: TimeSlot[];
}

interface OperatingHoursProps {
  daysOfWeek: string[];
  businessHours: BusinessHours;
  timezone: any;
  isBusinessHourAvailable: boolean;
}

export const OperatingHours = ({
  daysOfWeek,
  businessHours,
  timezone,
  isBusinessHourAvailable,
}: OperatingHoursProps) => {
  const t = useTranslations("OperatingHours");
  if (!isBusinessHourAvailable) {
    return (
      <div
        data-testid={applyDataTestId(pom.merchantFooter.operatingHoursSection)}
        className={[
          "flex",
          "flex-col",
          "w-[312px]",
          "max-md:ml-0",
          "justify-center",
          "items-start",
          "flex gap-4 justify-start items-start",
          "max-md:px-5",
        ].join(" ")}
      >
        {t("noBusinessHours")}
      </div>
    );
  }

  const now = new Date();
  const today = now.toLocaleString("en-US", { weekday: "long" }).toLowerCase();

  return (
    <div
      data-testid={applyDataTestId(pom.merchantFooter.operatingHoursSection)}
      className={[
        "flex",
        "flex-col",
        "w-[320px]",
        "max-md:ml-0",
        "max-md:w-full",
        "max-md:px-5",
        "gap-5",
      ].join(" ")}
    >
      <p
        className={["text-[20px]", "leading-[25px]", "font-semibold"].join(" ")}
      >
        {t("hours")}
      </p>
      <div
        data-testid={applyDataTestId(pom.footer.businessHours)}
        className={["w-full", "flex", "flex-col", "gap-2"].join(" ")}
      >
        {daysOfWeek.map((day: any) => {
          return (
            <div
              key={day}
              className={[
                "flex",
                "flex-row",
                "justify-between",
                day === today ? "font-bold" : "",
              ].join(" ")}
            >
              <span
                className={[
                  "capitalize",
                  "pr-4",
                  "text-[15px] leading-[22px]",
                ].join(" ")}
              >
                {day}
              </span>
              <div className="flex flex-col text-end">
                {businessHours[day as keyof BusinessHours].length > 0 ? (
                  businessHours[day as keyof BusinessHours].map(
                    (timeSlot: TimeSlot, index: number) => (
                      <span
                        key={index}
                        className={[
                          "text-[15px] leading-[22px]",
                          "text-end",
                        ].join(" ")}
                      >
                        {formatTime(timeSlot.start)} -{" "}
                        {formatTime(timeSlot.end)} {timezone}
                      </span>
                    )
                  )
                ) : (
                  <span>{t("closed")}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const formatTime = (time: any) => {
  const hours = Math.floor(time / 100);
  const minutes = time % 100;
  const period = hours >= 12 && hours < 24 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes.toString().padStart(2, "0");
  return `${formattedHours}:${formattedMinutes} ${period}`;
};
